class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    // Prevent duplicate transitionend listeners from piling up
    if (this._transitionEndHandler) {
      this.removeEventListener('transitionend', this._transitionEndHandler);
    }
    this._transitionEndHandler = () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    };
    this.addEventListener('transitionend', this._transitionEndHandler, { once: true });

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    this.classList.remove('animate');
    if (this._transitionEndHandler) {
      this.removeEventListener('transitionend', this._transitionEndHandler);
      this._transitionEndHandler = null;
    }
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.productId = parsedState.id;
    const itemCount = parsedState.item_count || 0;

    const cartIconBubbleHTML = parsedState.sections && parsedState.sections['cart-icon-bubble'];
    if (cartIconBubbleHTML) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = this.getSectionInnerHTML(cartIconBubbleHTML, '.shopify-section');
      }
    }

    const sectionHTML = parsedState.sections && parsedState.sections['cart-drawer'];
    if (sectionHTML) {
      const drawerInner = this.querySelector('.drawer__inner');
      if (drawerInner) {
        drawerInner.innerHTML = this.getSectionInnerHTML(sectionHTML, '.drawer__inner');
      }
    }

    // Keep empty-state classes in sync
    this.classList.toggle('is-empty', itemCount === 0);
    const cartDrawerItems = this.querySelector('cart-drawer-items');
    if (cartDrawerItems) {
      cartDrawerItems.classList.toggle('is-empty', itemCount === 0);
    }

    if (!this.classList.contains('active')) {
      this.open();
    }
  }

  getSectionInnerHTML(html, selector) {
    if (!html) return '';
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const el = parsed.querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

if (typeof CartItems === 'undefined') {
  console.error('CartItems is not defined. Make sure cart.js is loaded before cart-drawer.js.');
} else {
  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer-CartItems',
          section: 'cart-drawer',
          selector: '.js-contents',
        },
        {
          id: 'CartDrawer-Footer',
          section: 'cart-drawer',
          selector: '.cart-drawer__checkout-bar',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
      ];
    }
  }

  customElements.define('cart-drawer-items', CartDrawerItems);
}

/* ============================================================
   YMAL Upsell Add — uses the SAME partial-update path as
   quantity changes so YMAL never disappears and the drawer
   stays in a consistent state.
   ============================================================ */
document.addEventListener('click', function (e) {
  const btn = e.target.closest('[data-upsell-add]');
  if (!btn) return;

  e.preventDefault();
  e.stopPropagation();

  const variantId = btn.dataset.variantId;
  if (!variantId) return;

  const spinner = btn.querySelector('.loading__spinner');
  const btnText = btn.querySelector('span:not(.loading__spinner)');
  btn.disabled = true;
  if (btnText) btnText.style.display = 'none';
  if (spinner) spinner.classList.remove('hidden');

  const body = JSON.stringify({
    id: variantId,
    quantity: 1,
    sections: 'cart-drawer,cart-icon-bubble',
    sections_url: window.location.pathname,
  });

  fetch(window.routes.cart_add_url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
    body: body,
  })
    .then((res) => {
      if (!res.ok) return res.json().then((err) => { throw new Error(err.description || 'Add failed'); });
      return res.json();
    })
    .then((parsedState) => {
      // 1. Update bubble from the add response
      const cartIconBubbleHTML = parsedState.sections && parsedState.sections['cart-icon-bubble'];
      if (cartIconBubbleHTML) {
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble) {
          const parsed = new DOMParser().parseFromString(cartIconBubbleHTML, 'text/html');
          const el = parsed.querySelector('.shopify-section');
          if (el) cartIconBubble.innerHTML = el.innerHTML;
        }
      }

      // 2. Use the same partial-update path as quantity changes
      const cartDrawerItems = document.querySelector('cart-drawer-items');
      if (cartDrawerItems && typeof cartDrawerItems.onCartUpdate === 'function') {
        cartDrawerItems.onCartUpdate();
      }

      // 3. Open drawer if it was closed
      const cartDrawer = document.querySelector('cart-drawer');
      if (cartDrawer && !cartDrawer.classList.contains('active')) {
        cartDrawer.open();
      }
    })
    .catch((err) => {
      console.error('[Upsell]', err);
      alert(err.message || 'Could not add item. Please try again.');
    })
    .finally(() => {
      btn.disabled = false;
      if (btnText) btnText.style.display = '';
      if (spinner) spinner.classList.add('hidden');
    });
});